import {formatCurrency} from '@angular/common';
import {isNullOrUndefined} from 'util';
import {LastQuoteCacheService} from '../last-quote-cache.service';
import {makeOptionTicker, OptionType} from '../options-common/options.model';
import {QuoteDto} from '../shell-communication/dtos/quote-dto.class';
import {OptionExpirationDescriptor} from '../shell-communication/shell-dto-protocol';
import {TradingInstrument} from '../trading-instruments/trading-instrument.class';
import {MarketSide} from '../trading-model/market-side.enum';
import {findHCF, getShortUUID, isValidNumber, isVoid} from '../utils';
import {OptionsPricingPadComponent} from "./options-pricing-pad.component";


export class PricingPadLegWrapper {

    constructor(private _theLeg: PricingPadLeg) {
        if (!isVoid(_theLeg)) {
            this.linkId = _theLeg.linkId;
        }
    }

    linkId: string;

    get theLeg(): PricingPadLeg {
        return this._theLeg;
    }

    isDistance: boolean;

    private _distance?: number;
    get distance(): number {
        return this._distance;
    }

    set distance(value: number) {
        this._distance = value;
    }

    get optionType(): OptionType {
        return this._theLeg?.optionType
    }

    set optionType(value: OptionType) {
        this._theLeg.optionType = value;
    }

    get optionTypeString(): string {
        return this._theLeg?.optionTypeString
    }

    get expiration(): OptionExpirationDescriptor {
        return this._theLeg?.expiration;
    }

    set expiration(value: OptionExpirationDescriptor) {
        this._theLeg.expiration = value;
    }

    get strike(): number {
        return this._theLeg?.strike;
    }

    set strike(value: number) {
        this._theLeg.strike = value;
    }

    get marketSide(): MarketSide {
        return this._theLeg?.marketSide;
    }

    set marketSide(value: MarketSide) {
        this._theLeg.marketSide = value;
    }

    get marketSideString(): string {
        return this._theLeg.marketSideString;
    }

    get ticker(): string {
        return this._theLeg?.ticker;
    }

    get isStock(): boolean {
        return this._theLeg?.isStock;
    }

    set isStock(value: boolean) {
        this._theLeg.isStock = value;
    }

    get qty(): number {
        return this._theLeg?.qty;
    }

    set qty(value: number) {
        this._theLeg.qty = value;
    }

    get bid(): number {
        return this._theLeg?.bid;
    }

    set bid(value: number) {
        this._theLeg.bid = value;
    }

    get ask(): number {
        return this._theLeg?.ask;
    }

    set ask(value: number) {
        this._theLeg.ask = value;
    }

    get price(): number {
        return this._theLeg?.price;
    }

    set price(value: number) {
        this._theLeg.price = value;
    }

    get isSeparator(): boolean {
        return this._theLeg?.isSeparator;
    }

    isValid(): boolean {
        return this._theLeg.isValid();
    }
}


export class PricingPadLeg {

    constructor(public readonly underlying: TradingInstrument, isSeparator?: boolean) {
        this.linkId = getShortUUID();
        this.isSeparator = isSeparator;
    }

    private _ticker;
    private _strike: number;
    private _optionType: OptionType;
    private _expiration: OptionExpirationDescriptor;

    linkId: string;

    expectedStrike: number;

    get optionType(): OptionType {
        return this._optionType;
    }

    set optionType(v: OptionType) {
        if (this._optionType === v) {
            return;
        }
        this._optionType = v;
        this._ticker = undefined;
    }

    get expiration(): OptionExpirationDescriptor {
        return this._expiration;
    }

    set expiration(v: OptionExpirationDescriptor) {
        if (v === this._expiration) {
            return;
        }
        this._expiration = v;
        this._ticker = undefined;
    }

    get strike(): number {
        return this._strike;
    }

    set strike(v: number) {
        if (v === this._strike) {
            return;
        }
        this._strike = v;
        this._ticker = undefined;
    }

    marketSide?: MarketSide;
    isStock: boolean;
    qty?: number;
    bid?: number;
    ask?: number;
    price: number;
    isSeparator: boolean;

    get marketSideString(): string {
        return MarketSide[this.marketSide];
    }

    get optionTypeString(): string {
        if (this.isStock) {
            return null;
        }

        return OptionType[this.optionType];
    }

    get ticker(): string {

        if (this.isStock) {
            return this.underlying.ticker;
        }

        if (!this.strike || !this.optionType || !this.expiration) {
            return undefined;
        }

        if (!this._ticker) {
            this._ticker = makeOptionTicker(this.expiration, this.optionType, this.strike, 'American');
        }

        return this._ticker;
    }

    isValid(): boolean {
        let isValid = false;

        if (this.isStock) {

            isValid = !isNullOrUndefined(this.ticker);

        } else {

            isValid = !isNullOrUndefined(this.strike) && !isNaN(this.strike) && !isNullOrUndefined(this.expiration)
                && !isNullOrUndefined(this.optionType) && !isNullOrUndefined(this.ticker) && !isNullOrUndefined(this.marketSide);

        }

        return isValid;

    }

}


export class PricingPadStrategy {

    constructor(
        legs: PricingPadLeg[],
        public readonly expiration: OptionExpirationDescriptor,
        public readonly underlying: TradingInstrument,
        private readonly _lastQuoteCache: LastQuoteCacheService,
        private readonly _component: OptionsPricingPadComponent
    ) {
        if (legs && legs.length > 0) {
            this.setLegs(legs);
        }

        const dateParts = this.expiration.optionExpirationDisplayDate.split(' ');
        if (dateParts.length >= 2) {
            this.expirationDay = dateParts[0];
            this.expirationDate = dateParts[1];
        }
    }


    marketSideList: string[] = [
        'Buy',
        'Sell'
    ];

    optionTypes: string[] = [
        'Call',
        'Put',
    ];

    legs: PricingPadLeg[] = [];

    get sortedLegs(): PricingPadLeg[] {
        const calls: PricingPadLeg[] = [];
        const puts: PricingPadLeg[] = [];
        const stocks: PricingPadLeg[] = [];
        const unconfigured: PricingPadLeg[] = [];
        let separator: PricingPadLeg = null;

        // Separate by option type
        this.legs.forEach(leg => {
            if (leg.isSeparator) {
                separator = leg;
            } else if (leg.isStock) {
                stocks.push(leg);
            } else if (leg.optionType === OptionType.Call) {
                calls.push(leg);
            } else if (leg.optionType === OptionType.Put) {
                puts.push(leg);
            } else {
                // Empty/unconfigured legs (optionType undefined or Unknown)
                unconfigured.push(leg);
            }
        });

        // Sort each group by strike descending (highest first)
        const sortByStrike = (a: PricingPadLeg, b: PricingPadLeg) => {
            const aStrike = a.strike ?? -Infinity;
            const bStrike = b.strike ?? -Infinity;
            return bStrike - aStrike;  // Descending
        };

        calls.sort(sortByStrike);
        puts.sort(sortByStrike);

        // Assemble: unconfigured (first), separator, stocks, calls, separator (if exists), puts
        const result: PricingPadLeg[] = [...unconfigured];

        // Add synthetic separator after unconfigured legs if any exist
        if (unconfigured.length > 0) {
            const syntheticSeparator = new PricingPadLeg(this.underlying, true);
            result.push(syntheticSeparator);
        }

        result.push(...stocks, ...calls);

        if (separator) {
            result.push(separator);
        }

        result.push(...puts);

        return result;
    }

    getLegs(enableAutoSorting: boolean) {
        if (enableAutoSorting) {
            return this.sortedLegs;
        }
        return this.legs;
    }

    tickers: string[] = [];

    strategyCode: string;

    price: number;

    expirationDay: string;

    expirationDate: string;

    get expirationPriceColorObj() {
        const obj = {style: ''};
        if (this.price > 0) {
            obj.style = 'color: red';
        } else if (this.price < 0) {
            obj.style = 'color: green';
        } else {
            obj.style = 'color: ';
        }

        return obj;
    }

    get expirationPriceColor(): string {
        if (this.price > 0) {
            return 'red';
        }

        if (this.price < 0) {
            return 'green';
        }

        return undefined;
    }

    get expirationPriceString(): string {
        if (!this.expiration) {
            return;
        }

        if (!this.expiration.optionExpirationDisplayDate) {
            return;
        }

        let str = this.expiration.optionExpirationDisplayDate;

        if (this.price) {
            const pr = formatCurrency(Math.abs(this.price), 'en-US', '$');
            str += ` / ${pr}`;
        } else {
            str += ` / $--,--`;
        }

        return str;
    }

    get totalCost(): number {
        const hcf = findHCF(this.legs.map(x => Math.abs(x.qty)));
        return Math.abs(this.price) * 100 * hcf;
    }

    getStrategyCodeAndResetIt(): any {
        const sc = this.strategyCode;
        this.strategyCode = undefined;
        this.price = undefined;
        return sc;
    }

    clearLegs(): void {
        this.legs.length = 0;
        this.tickers = [];
        this.strategyCode = undefined;
        this.price = undefined;
    }


    setLegs(newLegs: PricingPadLeg[]): void {

        this.legs.length = 0;

        newLegs.forEach(x => {

            const aLeg = new PricingPadLeg(x.underlying);
            aLeg.linkId = x.linkId;
            aLeg.isStock = x.isStock;
            aLeg.isSeparator = x.isSeparator;
            aLeg.marketSide = x.marketSide;
            aLeg.qty = x.qty;
            aLeg.expiration = this.expiration;
            aLeg.strike = x.strike;
            aLeg.expectedStrike = x.expectedStrike;
            aLeg.optionType = x.optionType;

            if (this.expiration.strikes.indexOf(x.strike) < 0) {
                aLeg.strike = undefined;
            }

            this.legs.push(aLeg);
        });

        this.price = undefined;
        this.tickers = this.makeTickers(this.legs);
        this.strategyCode = this.makeStrategyCode(this.legs);
    }


    onQuote(quotes: QuoteDto[]): boolean {
        let detectChanges = false;

        this.legs.forEach(leg => {

            const quote = quotes.find(q => q.ticker === leg.ticker);

            if (quote) {
                leg.bid = quote.bid;
                leg.ask = quote.ask;

                const price = quote.mid;

                leg.price = isNaN(price) || price < Number.EPSILON ? undefined : price;

                detectChanges = true;
            }
        });

        return detectChanges;
    }

    addLeg(templateLeg: PricingPadLeg) {
        if (templateLeg.isStock && this.legs.find(x => x.isStock)) {
            return;
        }

        const leg = new PricingPadLeg(this.underlying);
        leg.linkId = templateLeg.linkId;
        leg.isStock = templateLeg.isStock;
        leg.marketSide = templateLeg.marketSide;
        leg.qty = templateLeg.qty;
        leg.expiration = this.expiration;
        leg.strike = templateLeg.strike;
        leg.expectedStrike = templateLeg.expectedStrike;
        leg.optionType = templateLeg.optionType;
        leg.price = templateLeg.price;

        if (leg.isStock) {
            this.legs.unshift(leg);
        } else {
            this.legs.push(leg);
        }

        this.price = undefined;

        if (leg.isValid()) {
            this.strategyCode = this.makeStrategyCode(this.legs);
        } else {
            this.strategyCode = undefined;
        }

        this.tickers = this.makeTickers(this.legs);
    }

    addEmptyLeg(legType: 'asset' | 'option'): PricingPadLeg {

        if (legType === 'asset' && this.legs.find(x => x.isStock)) {
            return;
        }

        const leg = new PricingPadLeg(this.underlying);
        leg.expiration = this.expiration;
        leg.isStock = legType === 'asset';

        if (leg.isStock) {
            this.legs.unshift(leg);
        } else {
            this.legs.push(leg);
        }

        this.price = undefined;
        this.strategyCode = undefined;
        this.tickers = this.makeTickers(this.legs);

        return leg;
    }


    removeLeg(legLinkId: string): PricingPadLeg[] {
        const ix = this.legs.findIndex(l => l.linkId === legLinkId);

        console.assert(ix >= 0, 'leg not found');

        if (ix < 0) {
            return;
        }

        const removed = this.legs.splice(ix, 1);

        this.price = undefined;
        this.tickers = this.makeTickers(this.legs);
        this.strategyCode = this.makeStrategyCode(this.legs);

        return removed;
    }

    duplicateLeg(leg: PricingPadLeg) {
        const dupLeg = new PricingPadLeg(leg.underlying);
        dupLeg.marketSide = leg.marketSide;
        dupLeg.qty = leg.qty;
        dupLeg.expiration = leg.expiration;

        // Apply strikeWidth from component if source has a valid strike
        const strikeWidth = this._component.strikeWidth;
        if (isValidNumber(strikeWidth, true) && isValidNumber(leg.strike, true)) {

            // NEAR LEG ANCHOR: Always duplicate AWAY from near leg
            // Call: near = lowest strike, so go higher (+width)
            // Put: near = highest strike, so go lower (-width)
            const multiplier = leg.optionType === OptionType.Call ? 1 : -1;

            // Calculate candidate strike and ensure it's unique
            let candidateStrike = leg.strike + strikeWidth * multiplier;

            // Get existing strikes ONLY from legs with the same option type.
            // This allows Calls and Puts to share strikes without interfering
            // (e.g., Iron Butterfly has both Call 450 AND Put 450).
            const existingStrikes = this.legs
                .filter(l => l !== leg
                             && l.optionType === leg.optionType  // Same option type only
                             && !l.isSeparator                    // Exclude separators
                             && isValidNumber(l.strike, true))
                .map(l => l.strike);

            // If candidate strike already exists, keep incrementing/decrementing until unique
            while (existingStrikes.includes(candidateStrike)) {
                candidateStrike += strikeWidth * multiplier;
            }

            dupLeg.strike = candidateStrike;
            dupLeg.expectedStrike = candidateStrike;

            // Insert position: always towards far end (away from near)
            // Call: near is low, far is high → insert BEFORE duplicated leg (towards beginning)
            // Put: near is high, far is low → insert AFTER duplicated leg (towards end)
            const ix = this.legs.indexOf(leg);
            if (ix >= 0) {
                const insertPosition = leg.optionType === OptionType.Call ? ix : ix + 1;
                this.legs.splice(insertPosition, 0, dupLeg);
            }
        } else {
            // No strikeWidth or no source strike - copy as-is and append
            dupLeg.strike = leg.strike;
            dupLeg.expectedStrike = leg.expectedStrike;
            const ix = this.legs.indexOf(leg);
            if (ix >= 0) {
                this.legs.splice(ix + 1, 0, dupLeg);
            }
        }

        dupLeg.optionType = leg.optionType;
        dupLeg.price = leg.price;

        this.price = undefined;

        if (dupLeg.isValid()) {
            this.strategyCode = this.makeStrategyCode(this.legs);
        } else {
            this.strategyCode = undefined;
        }
        this.tickers = this.makeTickers(this.legs);

        return dupLeg;
    }

    strikeUp(step: number, legLinkId: string, isLinked: boolean) {
        if (isLinked) {
            this.legs.forEach((leg) => this.upStrikeLeg(leg.linkId, step));
        } else {
            this.upStrikeLeg(legLinkId, step);
        }

        this.price = undefined;
        this.tickers = this.makeTickers(this.legs);
        this.strategyCode = this.makeStrategyCode(this.legs);
    }


    strikeDown(step: number, legLinkId: string, isLinked: boolean) {
        if (isLinked) {
            this.legs.forEach((leg) => this.downStrikeLeg(leg.linkId, step));
        } else {
            this.downStrikeLeg(legLinkId, step);
        }

        this.price = undefined;
        this.tickers = this.makeTickers(this.legs);
        this.strategyCode = this.makeStrategyCode(this.legs);
    }


    reverseLegsOption() {
        this.legs.forEach(x => {

            if (x.optionType === OptionType.Put) {
                x.optionType = OptionType.Call;
            } else if (x.optionType === OptionType.Call) {
                x.optionType = OptionType.Put;
            }
        });

        this.price = undefined;
        this.tickers = this.makeTickers(this.legs);
        this.strategyCode = this.makeStrategyCode(this.legs);
    }


    reverseLegsSide() {
        this.legs.forEach(x => {
            if (x.marketSide === MarketSide.Buy) {
                x.marketSide = MarketSide.Sell;
            } else if (x.marketSide === MarketSide.Sell) {
                x.marketSide = MarketSide.Buy;
            }
        });

        this.price = undefined;
        this.strategyCode = this.makeStrategyCode(this.legs);
    }


    changeLegOptionType(legLinkId: string, newType: 'Call' | 'Put', lqc: LastQuoteCacheService) {
        const targetLeg = this.legs.find(leg => leg.linkId === legLinkId);
        targetLeg.ask = targetLeg.bid = null;

        if (newType === 'Call') {
            targetLeg.optionType = OptionType.Call;
        } else if (newType === 'Put') {
            targetLeg.optionType = OptionType.Put;
        }

        const q = lqc.getLastQuote(targetLeg.ticker);
        if (q) {
            targetLeg.ask = q.ask;
            targetLeg.bid = q.bid;
            targetLeg.price = isNaN(q.mid) || q.mid < Number.EPSILON ? undefined : q.mid;
        }

        this.price = undefined;
        this.tickers = this.makeTickers(this.legs);
        this.strategyCode = this.makeStrategyCode(this.legs);
    }


    changeLegStrike(legLinkId: string, newStrike: number, lqc: LastQuoteCacheService) {

        const targetLeg = this.legs.find(leg => leg.linkId === legLinkId);

        targetLeg.ask = targetLeg.bid = null;

        targetLeg.strike = newStrike;
        targetLeg.expectedStrike = newStrike;

        if (this.expiration.strikes.indexOf(newStrike) < 0) {
            targetLeg.strike = undefined;
        }

        this.price = undefined;
        this.tickers = [];
        this.strategyCode = undefined;

        const q = lqc.getLastQuote(targetLeg.ticker);

        if (q) {
            targetLeg.ask = q.ask;
            targetLeg.bid = q.bid;
            targetLeg.price = q.mid;

            if (isNaN(targetLeg.price) || targetLeg.price < Number.EPSILON) {
                targetLeg.price = undefined;
            }
        }

        this.tickers = this.makeTickers(this.legs);
        this.strategyCode = this.makeStrategyCode(this.legs);
    }


    changeLegSide(legLinkId: string, value: any) {
        const targetLeg = this.legs.find(leg => leg.linkId === legLinkId);
        if (value === 'Buy') {
            targetLeg.marketSide = MarketSide.Buy;
        } else if (value === 'Sell') {
            targetLeg.marketSide = MarketSide.Sell;
        }

        this.price = undefined;
        this.strategyCode = this.makeStrategyCode(this.legs);
    }


    changeLegQty(legLinkId: string, value: any) {
        const targetLeg = this.legs.find(leg => leg.linkId === legLinkId);
        targetLeg.qty = value;

        this.price = undefined;
        this.strategyCode = this.makeStrategyCode(this.legs);
    }


    private downStrikeLeg(legLinkId: string, step: number) {
        const targetLeg = this.legs.find(l => l.linkId === legLinkId);

        if (targetLeg.isStock) {
            return;
        }

        const originalStrike = targetLeg.strike || targetLeg.expectedStrike;

        let newStrike = originalStrike - step;

        targetLeg.expectedStrike = newStrike;
        targetLeg.strike = newStrike;
    }


    private upStrikeLeg(legLinkId: string, step: number) {

        const targetLeg = this.legs.find(leg => leg.linkId === legLinkId);

        if (targetLeg.isStock) {
            return;
        }

        // const ix = this.expiration.strikes.indexOf(targetLeg.strike);
        //
        // if (ix < 0) {
        //    console.error('strike not found in list');
        //    return;
        // }

        const originalStrike = targetLeg.strike || targetLeg.expectedStrike;

        let newStrike = originalStrike + step;

        // if (!step) {
        //    let upIx = ix + 1;
        //
        //    if (upIx > this.expiration.strikes.length - 1) {
        //       upIx = this.expiration.strikes.length - 1;
        //    }
        //
        //    newStrike = this.expiration.strikes[upIx];
        //
        // } else {
        //
        //    const currentStrike = this.expiration.strikes[ix];
        //    const downStrike = currentStrike + step;
        //
        //    let downStrikeIx = this.expiration.strikes.indexOf(downStrike);
        //
        //    if (downStrikeIx === -1) {
        //       downStrikeIx = ix + 1;
        //    }
        //
        //    if (downStrikeIx > this.expiration.strikes.length - 1) {
        //       downStrikeIx = this.expiration.strikes.length - 1;
        //    }
        //
        //    newStrike = this.expiration.strikes[downStrikeIx];
        // }

        targetLeg.expectedStrike = newStrike;
        targetLeg.strike = newStrike;
    }


    private makeTickers(legs: PricingPadLeg[]): string[] {
        if (legs.length === 0) {
            return [];
        }

        return legs.filter(x => x.isValid())
            .map(x => x.ticker)
            .filter(x => !isNullOrUndefined(x)) || [];
    }


    private makeStrategyCode(legs: PricingPadLeg[]): string {
        legs = legs.filter(x => !x.isSeparator)

        if (legs.length === 0) {
            return undefined;
        }

        if (legs.some(x => !x.isValid())) {
            return undefined;
        }

        const codes = legs
            .slice()
            .sort((a, b) => a.strike - b.strike).map(x => `${x.qty * x.marketSide}|${x.ticker}`);

        const strategyCode = codes.join(';');

        if (!isNullOrUndefined(strategyCode)) {

            const slp = this._lastQuoteCache.getLastStrategyPrice(strategyCode);

            if (!isNullOrUndefined(slp)) {
                this.price = slp.price;
            }
        }

        return strategyCode;
    }

    getLegs2(enableAutoSorting: boolean): PricingPadLegWrapper[] {
        let legs = enableAutoSorting ? this.sortedLegs : this.legs;

        const pricingPadLegWrappers = legs.map(l => new PricingPadLegWrapper(l));

        const result = [];

        for (let i = 0; i < pricingPadLegWrappers.length; i++) {
            result.push(pricingPadLegWrappers[i]);

            // Add distance leg if there's a next leg
            if (i < pricingPadLegWrappers.length - 1) {

                const distance = Math.abs(pricingPadLegWrappers[i].strike - pricingPadLegWrappers[i + 1].strike);
                const dist = new PricingPadLegWrapper({} as any);
                dist.isDistance = true;
                dist.distance = isValidNumber(distance, true) ? distance : undefined;
                dist.optionType = pricingPadLegWrappers[i].optionType;
                dist.linkId = pricingPadLegWrappers[i].linkId + '+' + pricingPadLegWrappers[i + 1].linkId;
                result.push(dist);
            }
        }

        return result;
    }


    getLegs3(enableAutoSorting: boolean): PricingPadLegWrapper[] {

        const legs = enableAutoSorting ? this.sortedLegs : this.legs;

        if (!legs || legs.length === 0) {
            return [];
        }

        // Separate calls and puts
        const wrappers = legs.map(leg => new PricingPadLegWrapper(leg));

        const result: PricingPadLegWrapper[] = [];

        // Process calls (descending by strike - highest first)
        if (wrappers.length > 0) {
            const withDistances = insertDistanceLegs(wrappers);
            calculateMissingStrikes(withDistances);
            result.push(...withDistances);
        }
        return result;
    }
}

/**
 * Sorts legs, placing legs with null/undefined strikes at the end
 */
function sortLegsWithNulls(legs: PricingPadLegWrapper[], order: 'asc' | 'desc'): PricingPadLegWrapper[] {
    return [...legs].sort((a, b) => {
        const aHasStrike = a.strike != null;
        const bHasStrike = b.strike != null;

        // Put nulls at the end
        if (!aHasStrike && !bHasStrike) return 0;
        if (!aHasStrike) return 1;
        if (!bHasStrike) return -1;

        // Both have strikes, sort normally
        return order === 'desc'
            ? b.strike! - a.strike!
            : a.strike! - b.strike!;
    });
}


/**
 * Helper function to insert distance legs between sorted option legs
 */
function insertDistanceLegs(sortedLegs: PricingPadLegWrapper[]): PricingPadLegWrapper[] {
    const result: PricingPadLegWrapper[] = [];

    for (let i = 0; i < sortedLegs.length; i++) {
        result.push(sortedLegs[i]);

        // Add distance leg if there's a next leg
        if (i < sortedLegs.length - 1) {

            if (sortedLegs[i].isSeparator) {
                continue;
            }

            const nextLeg = sortedLegs[i + 1];

            if (isVoid(nextLeg)) {
                continue;
            }

            if (nextLeg.isSeparator) {
                continue;
            }

            const currentStrike = sortedLegs[i].strike;
            const nextStrike = sortedLegs[i + 1].strike;

            // Calculate distance only if both strikes are known
            let distance: number | null | undefined = undefined;
            if (currentStrike != null && nextStrike != null) {
                distance = Math.abs(currentStrike - nextStrike);
            }

            const distanceLeg = new PricingPadLegWrapper({} as any);
            distanceLeg.isDistance = true;
            distanceLeg.distance = distance;
            distanceLeg.optionType = sortedLegs[i].optionType;
            distanceLeg.linkId = sortedLegs[i].linkId + '+' + sortedLegs[i + 1].linkId;

            result.push(distanceLeg);
        }
    }

    return result;
}


/**
 * Recalculates all distance values based on current strikes
 */
function recalculateDistances(legs: PricingPadLegWrapper[]) {
    for (let i = 0; i < legs.length; i++) {

        if (!legs[i].isDistance) {
            continue;
        }

        const legAbove = legs[i - 1];

        const legBelow = legs[i + 1];

        if (!(legAbove && legBelow)) {
            continue;
        }

        const dist = Math.abs(legAbove.strike - legBelow.strike);

        // Distance 0 is valid (duplicate strikes), so don't use falseOnZero flag
        legs[i].distance = isValidNumber(dist, false) ? dist : undefined;
    }
}


/**
 * Updates strikes when a distance value is changed.
 * Also triggers calculation of missing strikes.
 * For Calls: strikes above the distance move up, strikes below stay fixed
 * For Puts: strikes below the distance move down, strikes above stay fixed
 */
export function updateStrikesFromDistance2(
    legs: PricingPadLegWrapper[],
    distanceIndex: number,
    newDistance: number
): PricingPadLegWrapper[] {
    if (!legs[distanceIndex] || !legs[distanceIndex].isDistance) {
        throw new Error('Invalid distance leg index');
    }

    const distanceLeg = legs[distanceIndex];
    const oldDistance = distanceLeg.distance;

    // If old distance was null/undefined, just set the new distance and calculate
    if (oldDistance == null) {
        const result = [...legs];
        result[distanceIndex].distance = newDistance;
        recalculateDistances(result);
        calculateMissingStrikes(result);
        return result;
    }

    const delta = newDistance - oldDistance;

    if (delta === 0) {
        return legs; // No change
    }

    const result = [...legs];
    const optionType = distanceLeg.optionType;

    // Update the distance leg
    result[distanceIndex].distance = newDistance;

    // Find the legs above and below the distance leg
    const legAboveIndex = distanceIndex - 1;
    const legBelowIndex = distanceIndex + 1;

    if (optionType === OptionType.Call) {
        // For calls (descending order): update all legs ABOVE the distance leg (move up)
        for (let i = 0; i <= legAboveIndex; i++) {
            if (!result[i].isDistance && result[i].strike != null) {
                result[i].strike = result[i].strike! + delta;
            }
        }
    } else {
        // For puts (ascending order): update all legs BELOW the distance leg (move down)
        for (let i = legBelowIndex; i < result.length; i++) {
            if (!result[i].isDistance && result[i].strike != null) {
                result[i].strike = result[i].strike! - delta;
            }
        }
    }

    recalculateDistances(result);

    // Recalculate all distances and missing strikes
    calculateMissingStrikes(result);

    return result;
}


/**
 * Calculates missing strikes based on known strikes and distances.
 * Propagates calculations in both directions from known strikes.
 */
function calculateMissingStrikes(legs: PricingPadLegWrapper[]): void {
    const result = [...legs];
    let hasChanges = true;

    // Keep iterating until no more strikes can be calculated
    while (hasChanges) {
        hasChanges = false;

        for (let i = 0; i < result.length; i++) {
            if (result[i].isDistance) continue;

            const currentLeg = result[i];

            // Skip if strike is already known
            if (currentLeg.strike != null) continue;

            // Try to calculate from leg above (if exists)
            if (i >= 2) {
                const distanceLegAbove = result[i - 1];
                const legAbove = result[i - 2];

                if (!legAbove.isDistance &&
                    legAbove.strike != null &&
                    distanceLegAbove.isDistance &&
                    distanceLegAbove.distance != null) {

                    if (currentLeg.optionType === OptionType.Call) {
                        // Calls are descending: leg above has higher strike
                        result[i].strike = legAbove.strike - distanceLegAbove.distance;
                    } else {
                        // Puts are ascending: leg above has lower strike
                        result[i].strike = legAbove.strike + distanceLegAbove.distance;
                    }
                    hasChanges = true;
                    continue;
                }
            }

            // Try to calculate from leg below (if exists)
            if (i < result.length - 2) {
                const distanceLegBelow = result[i + 1];
                const legBelow = result[i + 2];

                if (!legBelow.isDistance &&
                    legBelow.strike != null &&
                    distanceLegBelow.isDistance &&
                    distanceLegBelow.distance != null) {

                    if (currentLeg.optionType === OptionType.Call) {
                        // Calls are descending: leg below has lower strike
                        result[i].strike = legBelow.strike + distanceLegBelow.distance;
                    } else {
                        // Puts are ascending: leg below has higher strike
                        result[i].strike = legBelow.strike - distanceLegBelow.distance;
                    }
                    hasChanges = true;
                }
            }
        }
    }

    // Recalculate all distances after strikes are calculated
    recalculateDistances(result);
}